import { useRef, useCallback } from "react";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import useValidationErrors from "@util/hook/useValidationErrors";
import { isEmpty, isFunction } from "@util/utils";
import { useHistory } from "react-router-dom";
import { ACCOUNT_BOOK_ORDERS_ORDER_URL } from "@icoach/router/routerPath";

/**
 * 循環訂單首購提交 Hook
 * 負責處理首購訂單的 API 送出邏輯與回應處理
 *
 * @param {Object} options - 設定選項
 * @param {Function} [options.onSuccess] - 成功後的回調（可選）
 * @returns {Object} 提交函數和狀態
 */
const useCyclePurchaseFirstOrderSubmit = ({ onSuccess } = {}) => {
	const history = useHistory();
	const { postOrderCheckoutByCyclePurchaseApi } = useOrderCheckoutApi();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();

	const apiSendingRef = useRef(false);

	/**
	 * 提交首購訂單
	 * @param {Object} params - API 參數（由外部組裝）
	 * @returns {Promise<boolean>} 是否成功
	 */
	const submit = useCallback(
		async params => {
			if (apiSendingRef.current) return false;

			clearValidationErrors();
			apiSendingRef.current = true;

			try {
				const resp = await postOrderCheckoutByCyclePurchaseApi({
					cycleOrderID: params?.cycleOrderID ?? 0,
					clubID: params?.clubID ?? 0,
					memberID: params?.memberID ?? 0,
					employeeID: params?.employeeID ?? 0,
					orderDate: params?.orderDate,
					products: params?.products ?? [],
					cashPayment: params?.cashPayment ?? 0,
					cardPayment: params?.cardPayment ?? 0,
					invoiceSetting: {
						issuingType: params?.invoiceSetting?.issuingType ?? 0,
						invoiceCarrierType: params?.invoiceSetting?.invoiceCarrierType ?? 0,
						invoiceCarrierNo: params?.invoiceSetting?.invoiceCarrierNo ?? "",
						taxTitle: params?.invoiceSetting?.taxTitle ?? "",
						taxID: params?.invoiceSetting?.taxID ?? "",
						donationCode: params?.invoiceSetting?.donationCode ?? "",
						invoiceNo: params?.invoiceSetting?.invoiceNo ?? "",
						issuingEmail: params?.invoiceSetting?.issuingEmail ?? "",
					},
				});

				const { isError = false, result } = resp || {};

				if (isError) {
					// 0002 錯誤
					updateValidationErrors(result);
					apiSendingRef.current = false;
					return false;
				} else if (isEmpty(resp)) {
					apiSendingRef.current = false;
					return false;
				} else {
					const orderID = resp;
					// 成功
					if (isFunction(onSuccess)) {
						onSuccess(resp, params);
					}
					history.push(ACCOUNT_BOOK_ORDERS_ORDER_URL(orderID));
					apiSendingRef.current = false;
					return true;
				}
			} catch (error) {
				apiSendingRef.current = false;
				return false;
			}
		},
		// eslint-disable-next-line
		[postOrderCheckoutByCyclePurchaseApi, updateValidationErrors, clearValidationErrors, onSuccess],
	);

	/**
	 * 檢查是否正在提交中
	 */
	const isSubmitting = useCallback(() => {
		return apiSendingRef.current;
		// eslint-disable-next-line
	}, []);

	return {
		submit,
		isSubmitting,
		validationErrors,
		clearValidationErrors,
	};
};

export default useCyclePurchaseFirstOrderSubmit;
